import { MeteringPoint } from "components/ElectricityAgreements/models";

export enum ChargingAgreementType {
  ORGANIZATION = 'ORGANIZATION',
  INDIVIDUAL = 'INDIVIDUAL'
}

export interface ChargingAgreement {
  id: number;
  type: ChargingAgreementType;
  contractNumber: string;
  customer: ChargingAgreementCustomer;
  contactPerson: ChargingAgreementContactPerson;
  startDate?: string;
  endDate?: string;
  offerDeadline?: string;
  status: ChargingAgreementStatus;
  contractPeriod: CharingAgreementContractPeriod;
  extensionContractPeriod?: CharingAgreementContractPeriod;
  address?: string;
  city?: string;
  zip?: string;
  advancePaymentEuros?: number;
  hasEmergencyMaintenanceService: boolean
  hasChargerMaintenanceService: boolean;
  chargerMaintenanceType?: ChargingBillingCycle;
  rfidPaymentCardCount?: number;
  simCardCount?: number;
  simCardPaymentType?: ChargingBillingCycle;
  meteringPoint: MeteringPoint;
  externalChargingStationId: string;
  purchaseOption: ChargingPurchaseOption;
  serviceOption: ChargingServiceOption;
  thirdPartyCompensationOption: ChargingThirdPartyCompensationOption;
  marketPriceChargingPersonalIdentificationCodes?: string[];
  termsAndConditions: CharingTermsAndConditions;
  chargingStations: ChargingStation[];
  pricing: ChargingAgreementPricing;
  createCompensationTable: boolean;
  includePricesInCompensationTable: boolean;
  dateCreated: string;
  dateModified?: string;
}

export enum ContractPeriodType {
  INDEFINITE = 'INDEFINITE',
  PERIOD = 'PERIOD',
  FIXED_DATE = 'FIXED_DATE',
  TEMPORARY = 'TEMPORARY'
}

export interface CharingAgreementContractPeriod {
  id: number;
  type: ContractPeriodType,
  name: string;
}

export interface CharingTermsAndConditions {
  id: number;
  confirmationText: string;
}

export interface ChargingAgreementCustomer {
  id: number;
  name: string;
  registryNumber: string;
  address?: string;
  zip?: string;
  city?: string;
  county?: string;
  eic?: string;
}

export interface ChargingAgreementContactPerson {
  id: number;
  name: string;
  phone?: string;
  email?: string;
}

export interface ChargingAgreementPricing {
  installationFeeEuros?: number;
  devicesRentalMonthlyFeeEuros?: number;
  devicesFullServiceRentalMonthlyFeeEuros?: number;
  devicesCostEuros?: number;
  thirdPartyCompensationNetoFeeEuros?: number;
  thirdPartyCompensationNetoMarginalCentsKwh?: number
  maintenanceMonthlyFeeEuros?: number;
  rfidPaymentCardMonthlyFeeEuros?: number;
  simCardMonthlyFeeEuros?: number;
  mediationAndBillingMonthlyFeePerNozzleEuros?: number;
  infrastructureUsageOneTimeFeePerNozzleEuros?: number;
  infrastructureUsageMonthlyFeePerNozzleEuros?: number;
  chargingServiceMonthlyFeeEuros?: number;
  thirdPartyChargingMarginalCentsKwh?: number;
  gridDayTariffCentsKwh?: number;
  gridNightTariffCentsKwh?: number;
  gridExciseDutyCentsKwh?: number;
  gridRenewableEnergyChargeCentsKwh?: number;
  acPublicChargingPriceEurosKwh?: number;
}

export interface SimplifiedChargingAgreement {
  id: number;
  name: string;
  startDate: string;
}

export interface ChargingStation {
  id: number;
  serialNumber: string;
  latitude?: string;
  longitude: string;
  installationDate?: string;
  installationExtraFeeEuros?: number;
  nozzleCount?: number;
  model: ChargingStationModel;
  externalId?: string;
  thirdPartyCompensationOption?: ChargingThirdPartyCompensationOption;
  marketPriceChargingPersonalIdentificationCodes?: string[];
  personalIdentificationCode?: string;
}

export enum ChargingAgreementStatus {
  ACTIVE = 'ACTIVE',
  ENDED = 'ENDED',
  PENDING = 'PENDING',
  REJECTED = 'REJECTED',
  CANCELED = 'CANCELED'
}

export const CHARGING_AGREEMENT_STATUS_MAP: Record<ChargingAgreementStatus, string> = {
  ACTIVE: 'Aktiivne',
  ENDED: 'Lõppenud',
  PENDING: 'Ootel',
  REJECTED: 'Tagasilükatud',
  CANCELED: 'Tühistatud'
};

export enum ChargingAgreementSortFields {
  ID = "ID",
  DATE_MODIFIED = "DATE_MODIFIED",
  START_DATE = 'START_DATE',
  END_DATE = 'END_DATE',
  DATE_CREATED = 'DATE_CREATED'
}

export interface ChargingAgreementFilters {
  search?: string;
  status?: ChargingAgreementStatus;
}

export enum ChargingPurchaseOptionType {
  PURCHASE = 'PURCHASE',
  RENT = 'RENT',
  FULL_SERVICE_RENT = 'FULL_SERVICE_RENT',
  EXISTING = 'EXISTING',
  SERVICE_PROVIDER = 'SERVICE_PROVIDER'
}

export interface ChargingPurchaseOption {
  id: number;
  name: string;
  description?: string;
  type: ChargingPurchaseOptionType
}

export enum ChargingServiceOptionType {
  PUBLIC = 'PUBLIC',
  SEMI_PUBLIC = 'SEMI_PUBLIC',
  PRIVATE_AUTHENTICATED = 'PRIVATE_AUTHENTICATED',
  PRIVATE_NON_AUTHENTICATED = 'PRIVATE_NON_AUTHENTICATED'
}

export interface ChargingServiceOption {
  id: number;
  name: string;
  description?: string;
  type: ChargingServiceOptionType
}

export enum ChargingThirdPartyCompensationOptionType {
  FIXED = 'FIXED',
  TARIFF = 'TARIFF',
  NETO = 'NETO'
}

export interface ChargingThirdPartyCompensationOption {
  id: number;
  name: string;
  description?: string;
  type: ChargingThirdPartyCompensationOptionType
}

export interface ChargingStationModel {
  id: number;
  name: string;
  description?: string;
  rentalMonthlyFeeEuros: number;
  priceEuros: number;
}

export enum ChargingBillingCycle {
  YEARLY = 'YEARLY',
  QUARTERLY = 'QUARTERLY',
  MONTHLY = 'MONTHLY'
}

export const chargingBillingCycleOptions = [
  {value: ChargingBillingCycle.YEARLY, label : "Kord aastas"},
  {value: ChargingBillingCycle.QUARTERLY, label : "Kord kvartalis"},
  {value: ChargingBillingCycle.MONTHLY, label : "Kord kuus"}
]